import React from 'react';

const FORM_KEYS = ['ban', 'timeout', 'mute'];
const PRIMARY_TABS = ['ban', 'timeout', 'mute', 'dmOnJoin'];

function prettyFormLabel(formKey) {
  if (formKey === 'timeout') return 'Timeout Appeals';
  if (formKey === 'mute') return 'Mute Appeals';
  return 'Ban Appeals';
}

function prettyPrimaryTabLabel(key) {
  if (key === 'dmOnJoin') return 'DM On Join';
  return prettyFormLabel(key);
}

function IconBox({ src, name, inBot }) {
  const commonStyle = {
    width: 96,
    height: 96,
    borderRadius: 24,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--card)',
    border: '1px solid var(--border)',
    filter: inBot ? 'none' : 'grayscale(100%)',
    opacity: inBot ? 1 : 0.7,
    margin: '0 auto',
  };

  if (src) {
    return (
      <div style={commonStyle} aria-hidden="true">
        <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
    );
  }

  const letter = (name || '?').trim().charAt(0).toUpperCase();
  return (
    <div style={commonStyle} aria-hidden="true">
      <span style={{ fontWeight: 700, fontSize: 28, color: 'var(--muted)' }}>{letter}</span>
    </div>
  );
}

function amountUnitToMs(amount, unit) {
  const n = Math.max(0, parseInt(amount || 0, 10));
  const u = String(unit || 'Minutes').toLowerCase();
  const mult = {
    seconds: 1000,
    minutes: 60_000,
    hours: 3_600_000,
    days: 86_400_000,
    weeks: 604_800_000,
    months: 2_592_000_000,
    years: 31_557_600_000,
  }[u] ?? 60_000;
  return n * mult;
}

function msToAmountUnit(ms) {
  const units = [
    ['Years', 31_557_600_000],
    ['Months', 2_592_000_000],
    ['Weeks', 604_800_000],
    ['Days', 86_400_000],
    ['Hours', 3_600_000],
    ['Minutes', 60_000],
    ['Seconds', 1000],
  ];

  if (!ms || ms < 1000) return { amount: 0, unit: 'Minutes' };

  for (const [name, size] of units) {
    if (ms % size === 0) return { amount: ms / size, unit: name };
  }

  return { amount: Math.round(ms / 60_000), unit: 'Minutes' };
}

function createBlankFormState() {
  return {
    enabled: false,
    appealChannel: 'none',
    appealDelay: 0,
    delayAmount: 0,
    delayUnit: 'Minutes',
    questions: [''],
    mutedRoleId: '',
    messages: {
      notifyText: '',
      acceptedText: '',
      deniedText: '',
    },
  };
}

function normalizeFormState(formKey, raw) {
  const state = createBlankFormState();
  const delay = msToAmountUnit(raw?.appealDelay || 0);

  return {
    ...state,
    enabled: !!raw?.enabled,
    appealChannel: raw?.appealChannel || 'none',
    appealDelay: raw?.appealDelay || 0,
    delayAmount: delay.amount,
    delayUnit: delay.unit,
    questions: (raw?.questions && raw.questions.length ? raw.questions : ['']).slice(0, 5),
    mutedRoleId: formKey === 'mute' ? raw?.mutedRoleId || '' : '',
    messages: {
      notifyText: raw?.messages?.notifyText || '',
      acceptedText: raw?.messages?.acceptedText || '',
      deniedText: raw?.messages?.deniedText || '',
    },
  };
}

function ServerCard({ item, onClick }) {
  const { name, icon, inBot } = item;
  return (
    <button
      className="card"
      onClick={onClick}
      style={{ textAlign: 'center', paddingTop: 24, paddingBottom: 24, cursor: 'pointer' }}
      aria-label={`Open server ${name}`}
    >
      <IconBox src={icon} name={name} inBot={inBot} />
      <div className="title" style={{ marginTop: 12, fontSize: 18 }}>{name}</div>
      {!inBot && <div className="muted" style={{ marginTop: 6 }}>Invite to server</div>}
    </button>
  );
}

function SettingsQuestionsPanel({ guild, onClose, onSaved }) {
  const [loading, setLoading] = React.useState(true);
  const [notice, setNotice] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState('settings');
  const [activePrimaryTab, setActivePrimaryTab] = React.useState('ban');
  const [saving, setSaving] = React.useState(false);
  const panelRef = React.useRef(null);

  const [channels, setChannels] = React.useState([]);
  const [roles, setRoles] = React.useState([]);
  const [dmOnJoin, setDmOnJoin] = React.useState(true);
  const [dmOnJoinText, setDmOnJoinText] = React.useState('');
  const [forms, setForms] = React.useState({
    ban: createBlankFormState(),
    timeout: createBlankFormState(),
    mute: createBlankFormState(),
  });

  const isDmOnJoinTab = activePrimaryTab === 'dmOnJoin';
  const activeForm = FORM_KEYS.includes(activePrimaryTab) ? activePrimaryTab : 'ban';
  const currentForm = forms[activeForm] || createBlankFormState();

  const setCurrentForm = React.useCallback((patch) => {
    setForms((prev) => ({
      ...prev,
      [activeForm]: {
        ...prev[activeForm],
        ...patch,
      },
    }));
  }, [activeForm]);

  const setCurrentFormMessages = React.useCallback((patch) => {
    setForms((prev) => ({
      ...prev,
      [activeForm]: {
        ...prev[activeForm],
        messages: {
          ...prev[activeForm].messages,
          ...patch,
        },
      },
    }));
  }, [activeForm]);

  const scrollPanelToTop = React.useCallback(() => {
    const el = panelRef.current;
    if (!el) return;
    if (typeof el.scrollTo === 'function') {
      el.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      el.scrollTop = 0;
    }
  }, []);

  const loadData = React.useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch(`/api/servers/${guild.id}/config`, { credentials: 'include' }).then((r) => r.json()),
      fetch(`/api/servers/${guild.id}/channels`, { credentials: 'include' }).then((r) => r.json()),
      fetch(`/api/servers/${guild.id}/roles`, { credentials: 'include' }).then((r) => r.json()),
    ])
      .then(([cfg, ch, roleData]) => {
        if (cfg.error) {
          setNotice({ type: 'error', text: 'Failed to load server configuration.' });
          return;
        }

        setDmOnJoin(!!cfg.dmOnJoin);
        setDmOnJoinText(cfg.dmOnJoinText || '');
        setChannels(ch.channels || []);
        setRoles(roleData.roles || []);
        setForms({
          ban: normalizeFormState('ban', cfg.forms?.ban),
          timeout: normalizeFormState('timeout', cfg.forms?.timeout),
          mute: normalizeFormState('mute', cfg.forms?.mute),
        });
      })
      .finally(() => setLoading(false));
  }, [guild.id]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const addQuestion = () => {
    if (currentForm.questions.length >= 5) return;
    const last = currentForm.questions[currentForm.questions.length - 1] || '';
    if (!last.trim()) {
      setNotice({ type: 'warn', text: 'Please fill in the current question before adding another.' });
      return;
    }
    setCurrentForm({ questions: [...currentForm.questions, ''] });
  };

  const removeQuestion = (index) => {
    if (index === 0) return;
    setCurrentForm({ questions: currentForm.questions.filter((_, i) => i !== index) });
  };

  const handleQuestionChange = (index, value) => {
    setCurrentForm({
      questions: currentForm.questions.map((question, i) => (i === index ? value.slice(0, 45) : question)),
    });
  };

  const saveSettings = async () => {
    setNotice(null);
    setSaving(true);

    try {
      const response = await fetch(`/api/servers/${guild.id}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          formKey: activeForm,
          enabled: currentForm.enabled,
          appealChannel: String(currentForm.appealChannel || '').trim(),
          appealDelay: amountUnitToMs(currentForm.delayAmount, currentForm.delayUnit),
          mutedRoleId: activeForm === 'mute' ? String(currentForm.mutedRoleId || '').trim() : '',
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setNotice({ type: 'success', text: `${prettyFormLabel(activeForm)} settings saved.` });
        onSaved?.();
        loadData();
        return;
      }

      let text = 'Failed to save settings.';

      if (data.error === 'need_appeal_channel') {
        text = 'Please set an appeal channel before enabling this form.';
        setCurrentForm({ enabled: false });
      } else if (data.error === 'need_questions') {
        text = 'Please add at least one question before enabling this form.';
        setCurrentForm({ enabled: false });
      } else if (data.error === 'bot_not_in_guild') {
        text = 'The bot is not currently in this server.';
      } else if (data.error === 'missing_channel_perms') {
        text = `The bot cannot use the selected appeal channel yet.\n• ${(data.missing || []).join('\n• ')}`;
        setCurrentForm({ enabled: false });
      } else if (data.error === 'invalid_appeal_channel') {
        text = `That appeal channel cannot be saved.\n• ${(data.missing || []).join('\n• ')}`;
      } else if (data.error === 'invalid_muted_role') {
        text = `That muted role cannot be saved.\n• ${(data.missing || []).join('\n• ')}`;
        setCurrentForm({ enabled: false });
      } else if (data.error === 'missing_guild_perms') {
        text = `The bot cannot enable ${prettyFormLabel(activeForm)} yet.\n• ${(data.missing || []).join('\n• ')}`;
        setCurrentForm({ enabled: false });
      }

      setNotice({ type: 'error', text });
    } finally {
      setSaving(false);
      scrollPanelToTop();
    }
  };

  const saveQuestions = async () => {
    setNotice(null);

    let questions = currentForm.questions.map((question) => String(question ?? '').trim());
    if (questions.some((question) => question.length === 0)) {
      setNotice({ type: 'error', text: 'All question fields must be filled in with 1–45 characters.' });
      return;
    }
    if (questions.length < 1) {
      setNotice({ type: 'error', text: 'Please add at least one question.' });
      return;
    }
    if (questions.length > 5) questions = questions.slice(0, 5);
    if (questions.some((question) => question.length > 45)) {
      setNotice({ type: 'error', text: 'Each question must be 45 characters or fewer.' });
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`/api/servers/${guild.id}/questions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ formKey: activeForm, questions }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setNotice({ type: 'success', text: `${prettyFormLabel(activeForm)} questions saved.` });
        setCurrentForm({ questions });
        onSaved?.();
      } else {
        let text = 'Failed to save questions.';
        if (data.error === 'invalid_length') text = 'Each question must be 1–45 characters.';
        if (data.error === 'need_at_least_one') text = 'Please add at least one question.';
        if (data.error === 'bot_not_in_guild') text = 'The bot is not currently in this server.';
        setNotice({ type: 'error', text });
      }
    } finally {
      setSaving(false);
      scrollPanelToTop();
    }
  };

  const saveMessages = async () => {
    setNotice(null);

    const payload = {
      formKey: activeForm,
      notifyText: (currentForm.messages.notifyText || '').trim(),
      acceptedText: (currentForm.messages.acceptedText || '').trim(),
      deniedText: (currentForm.messages.deniedText || '').trim(),
    };

    for (const value of Object.values(payload)) {
      if (typeof value === 'string' && value.length > 1024) {
        setNotice({ type: 'error', text: 'Each message must be 1024 characters or fewer.' });
        return;
      }
    }

    setSaving(true);
    try {
      const response = await fetch(`/api/servers/${guild.id}/messages`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setNotice({
          type: 'success',
          text: `${prettyFormLabel(activeForm)} messages saved. Blank fields will use the bot defaults.`,
        });
        onSaved?.();
      } else {
        let text = 'Failed to save messages.';
        if (data.error === 'invalid_length') text = 'Each message must be 1024 characters or fewer.';
        if (data.error === 'bot_not_in_guild') text = 'The bot is not currently in this server.';
        setNotice({ type: 'error', text });
      }
    } finally {
      setSaving(false);
      scrollPanelToTop();
    }
  };

  const saveDmOnJoin = async () => {
    setNotice(null);

    const trimmed = dmOnJoinText.trim();
    if (trimmed.length > 1024) {
      setNotice({ type: 'error', text: 'The DM on join message must be 1024 characters or fewer.' });
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`/api/servers/${guild.id}/dm-on-join`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          dmOnJoin,
          dmOnJoinText: trimmed,
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setNotice({
          type: 'success',
          text: 'DM on join settings saved. A blank message will use the default bot message.',
        });
        onSaved?.();
        loadData();
      } else {
        let text = 'Failed to save DM on join settings.';
        if (data.error === 'invalid_length') text = 'The DM on join message must be 1024 characters or fewer.';
        if (data.error === 'bot_not_in_guild') text = 'The bot is not currently in this server.';
        setNotice({ type: 'error', text });
      }
    } finally {
      setSaving(false);
      scrollPanelToTop();
    }
  };

  const tabButtonStyle = (tab) => ({
    padding: '6px 12px',
    borderRadius: 999,
    border: '1px solid var(--border)',
    background: activeTab === tab ? 'var(--card)' : 'transparent',
    color: 'var(--fg)',
    cursor: 'pointer',
    fontSize: 14,
  });

  const primaryButtonStyle = (key) => ({
    padding: '8px 12px',
    borderRadius: 999,
    border: '1px solid var(--border)',
    background: activePrimaryTab === key ? 'var(--card)' : 'transparent',
    color: 'var(--fg)',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: activePrimaryTab === key ? 700 : 500,
  });

  const renderAppealSettings = () => (
    <div className="card">
      <h4 className="title" style={{ marginTop: 0 }}>{prettyFormLabel(activeForm)} Settings</h4>

      <div style={{ display: 'grid', gap: 12 }}>
        <label>
          <span className="title" style={{ fontWeight: 600 }}>Enable Form</span>
          <br />
          <input
            type="checkbox"
            checked={currentForm.enabled}
            onChange={(e) => setCurrentForm({ enabled: e.target.checked })}
          />{' '}
          <span className="muted">Accept {prettyFormLabel(activeForm).toLowerCase()} for this server.</span>
        </label>

        <label>
          <span className="title" style={{ fontWeight: 600 }}>Appeal Channel</span>
          <br />
          <select
            value={currentForm.appealChannel}
            onChange={(e) => setCurrentForm({ appealChannel: e.target.value })}
            style={{
              width: '100%',
              background: 'var(--card)',
              color: 'var(--fg)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: 8,
            }}
          >
            <option value="none">Select a channel…</option>
            {channels.map((channel) => (
              <option key={channel.id} value={channel.id}>{channel.name}</option>
            ))}
          </select>
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            This is the staff-only channel where {prettyFormLabel(activeForm).toLowerCase()} will be posted.
          </div>
        </label>

        <label>
          <span className="title" style={{ fontWeight: 600 }}>Wait Time</span>
          <br />
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="number"
              min={0}
              value={currentForm.delayAmount}
              onChange={(e) => setCurrentForm({ delayAmount: e.target.value })}
              style={{
                flex: '0 0 120px',
                background: 'var(--card)',
                color: 'var(--fg)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: 8,
              }}
            />
            <select
              value={currentForm.delayUnit}
              onChange={(e) => setCurrentForm({ delayUnit: e.target.value })}
              style={{
                flex: '1',
                background: 'var(--card)',
                color: 'var(--fg)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: 8,
              }}
            >
              {['Seconds', 'Minutes', 'Hours', 'Days', 'Weeks', 'Months', 'Years'].map((unit) => (
                <option key={unit} value={unit}>{unit}</option>
              ))}
            </select>
          </div>
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            How long users must wait after the moderation action before they can submit this form.
          </div>
        </label>

        {activeForm === 'mute' && (
          <label>
            <span className="title" style={{ fontWeight: 600 }}>Muted Role</span>
            <br />
            <select
              value={currentForm.mutedRoleId || ''}
              onChange={(e) => setCurrentForm({ mutedRoleId: e.target.value })}
              style={{
                width: '100%',
                background: 'var(--card)',
                color: 'var(--fg)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: 8,
              }}
            >
              <option value="">Select a role…</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}{role.managed ? ' (managed)' : ''}
                </option>
              ))}
            </select>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              The selected role must be manageable by the bot before mute appeals can be enabled or saved.
            </div>
          </label>
        )}

        <div style={{ textAlign: 'right' }}>
          <button className="button" onClick={saveSettings} disabled={saving}>Save settings</button>
        </div>
      </div>
    </div>
  );

  const renderQuestions = () => (
    <div className="card">
      <h4 className="title" style={{ marginTop: 0 }}>{prettyFormLabel(activeForm)} Questions</h4>

      <div style={{ display: 'grid', gap: 10 }}>
        {currentForm.questions.map((question, index) => (
          <div key={index}>
            <label className="title" style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>
              Question {index + 1}
            </label>
            <input
              value={question}
              onChange={(e) => handleQuestionChange(index, e.target.value)}
              maxLength={45}
              placeholder="1–45 characters"
              style={{
                width: '100%',
                background: 'var(--card)',
                color: 'var(--fg)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: 8,
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="muted" style={{ fontSize: 12 }}>{question.length}/45</span>
              {index > 0 && (
                <button
                  className="button"
                  onClick={() => removeQuestion(index)}
                  style={{ background: 'transparent', color: 'var(--fg)', border: '1px solid var(--border)' }}
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 12 }}>
        <button
          className="button"
          onClick={addQuestion}
          disabled={saving || currentForm.questions.length >= 5}
          style={{ background: 'transparent', color: 'var(--fg)', border: '1px solid var(--border)' }}
        >
          Add question
        </button>
        <button className="button" onClick={saveQuestions} disabled={saving}>Save questions</button>
      </div>
    </div>
  );

  const renderMessages = () => (
    <div className="card">
      <h4 className="title" style={{ marginTop: 0 }}>{prettyFormLabel(activeForm)} Messages</h4>
      <p className="muted" style={{ marginTop: 0, marginBottom: 12 }}>
        Leave any field blank to use the default bot message for this form.
      </p>

      {[
        { label: 'Notification Message', key: 'notifyText' },
        { label: 'Accepted Message', key: 'acceptedText' },
        { label: 'Denied Message', key: 'deniedText' },
      ].map(({ label, key }) => (
        <div key={key} style={{ marginBottom: 12 }}>
          <label className="title" style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>
            {label}
          </label>
          <textarea
            value={currentForm.messages[key]}
            onChange={(e) => setCurrentFormMessages({ [key]: e.target.value.slice(0, 1024) })}
            rows={4}
            style={{
              width: '100%',
              background: 'var(--card)',
              color: 'var(--fg)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: 8,
              resize: 'vertical',
            }}
            maxLength={1024}
          />
          <div className="muted" style={{ fontSize: 12, textAlign: 'right' }}>
            {(currentForm.messages[key] || '').length}/1024
          </div>
        </div>
      ))}

      <div style={{ textAlign: 'right', marginTop: 8 }}>
        <button className="button" onClick={saveMessages} disabled={saving}>Save messages</button>
      </div>
    </div>
  );

  const renderDmOnJoin = () => (
    <div className="card">
      <h4 className="title" style={{ marginTop: 0 }}>DM On Join</h4>
      <p className="muted" style={{ marginTop: 0, marginBottom: 14 }}>
        Leave the message blank to use the default DM on join message.
      </p>

      <div style={{ display: 'grid', gap: 14 }}>
        <label>
          <span className="title" style={{ fontWeight: 600 }}>Enable DM on Join</span>
          <br />
          <input type="checkbox" checked={dmOnJoin} onChange={(e) => setDmOnJoin(e.target.checked)} />{' '}
          <span className="muted">Send a DM when a user joins the server.</span>
        </label>

        <div>
          <label className="title" style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>
            DM on Join Message
          </label>
          <textarea
            value={dmOnJoinText}
            onChange={(e) => setDmOnJoinText(e.target.value.slice(0, 1024))}
            rows={5}
            style={{
              width: '100%',
              background: 'var(--card)',
              color: 'var(--fg)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: 8,
              resize: 'vertical',
            }}
            maxLength={1024}
          />
          <div className="muted" style={{ fontSize: 12, textAlign: 'right' }}>
            {(dmOnJoinText || '').length}/1024
          </div>
        </div>

        <div style={{ textAlign: 'right' }}>
          <button className="button" onClick={saveDmOnJoin} disabled={saving}>Save DM on join</button>
        </div>
      </div>
    </div>
  );

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        paddingTop: '80px',
        paddingBottom: '24px',
        zIndex: 20,
      }}
    >
      <div
        className="card"
        ref={panelRef}
        style={{
          width: '95vw',
          maxWidth: 1100,
          height: 'min(720px, calc(100vh - 120px))',
          overflow: 'auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginBottom: 12,
          }}
        >
          <h3 className="title" style={{ margin: 0 }}>
            {guild.name} – Settings
          </h3>
          <button
            className="button"
            style={{ background: 'transparent', color: 'var(--fg)', border: '1px solid var(--border)' }}
            onClick={onClose}
          >
            Close
          </button>
        </div>

        <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {PRIMARY_TABS.map((key) => (
            <button
              key={key}
              style={primaryButtonStyle(key)}
              onClick={() => {
                setActivePrimaryTab(key);
                setNotice(null);
              }}
            >
              {prettyPrimaryTabLabel(key)}
            </button>
          ))}
        </div>

        {!isDmOnJoinTab && (
          <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={tabButtonStyle('settings')} onClick={() => setActiveTab('settings')}>Settings</button>
            <button style={tabButtonStyle('questions')} onClick={() => setActiveTab('questions')}>Questions</button>
            <button style={tabButtonStyle('messages')} onClick={() => setActiveTab('messages')}>Messages</button>
          </div>
        )}

        {loading ? (
          <p className="muted">Loading configuration…</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {notice && (
              <div
                className="card"
                style={{
                  whiteSpace: 'pre-line',
                  borderColor:
                    notice.type === 'error'
                      ? '#b91c1c'
                      : notice.type === 'success'
                      ? '#16a34a'
                      : 'var(--border)',
                  background:
                    notice.type === 'error'
                      ? 'rgba(248, 113, 113, 0.08)'
                      : notice.type === 'success'
                      ? 'rgba(74, 222, 128, 0.08)'
                      : 'var(--card)',
                }}
              >
                <p style={{ margin: 0 }}>{notice.text}</p>
              </div>
            )}

            {isDmOnJoinTab ? renderDmOnJoin() : null}
            {!isDmOnJoinTab && activeTab === 'settings' ? renderAppealSettings() : null}
            {!isDmOnJoinTab && activeTab === 'questions' ? renderQuestions() : null}
            {!isDmOnJoinTab && activeTab === 'messages' ? renderMessages() : null}
          </div>
        )}
      </div>
    </div>
  );
}

export default function MyServers() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [panelGuild, setPanelGuild] = React.useState(null);

  React.useEffect(() => {
    setLoading(true);
    fetch('/api/servers/my', { credentials: 'include' })
      .then((response) => response.json())
      .then((data) => setItems(data.items || []))
      .finally(() => setLoading(false));
  }, []);

  const handleClick = (item) => {
    if (item.inBot) {
      setPanelGuild(item);
    } else {
      window.open(item.inviteUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) return <div className="card">Loading your servers…</div>;

  if (!items.length) {
    return (
      <div className="card" style={{ textAlign: 'center' }}>
        <h3 className="title" style={{ marginTop: 0 }}>No manageable servers</h3>
        <p className="muted">You do not have the “Manage Server” permission in any servers linked to your account.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="grid servers-grid">
        {items.map((item) => (
          <ServerCard key={item.id} item={item} onClick={() => handleClick(item)} />
        ))}
      </div>

      {panelGuild && (
        <SettingsQuestionsPanel
          guild={panelGuild}
          onClose={() => setPanelGuild(null)}
          onSaved={() => {}}
        />
      )}
    </div>
  );
}
